import { useTranslation } from 'react-i18next';
import PublicContentPage, { PublicCtaLink } from '../components/layout/PublicContentPage';
import { buildArticleSchema } from '../lib/seo';

const GUIDE_PATH = '/how-do-i-make-my-website-ada-compliant';
const GUIDE_DATE = '2026-05-21';

interface GuideSection {
  title: string;
  body: string;
  points?: string[];
}

interface GuideStep {
  title: string;
  body: string;
}

interface GuideAction {
  href: string;
  label: string;
  variant: 'primary' | 'secondary' | 'ghost';
}

export default function AdaComplianceGuidePage() {
  const { t } = useTranslation('marketing');
  const checklist = t('pages.adaComplianceGuide.checklist.items', { returnObjects: true }) as string[];
  const steps = t('pages.adaComplianceGuide.process.steps', { returnObjects: true }) as GuideStep[];
  const sections = t('pages.adaComplianceGuide.sections', { returnObjects: true }) as GuideSection[];
  const sidebarPoints = t('pages.adaComplianceGuide.sidebar.points', { returnObjects: true }) as string[];
  const ctaActions = t('pages.adaComplianceGuide.cta.actions', { returnObjects: true }) as GuideAction[];

  return (
    <PublicContentPage
      metaTitle={t('pages.adaComplianceGuide.meta.title')}
      metaDescription={t('pages.adaComplianceGuide.meta.description')}
      canonicalPath={GUIDE_PATH}
      eyebrow={t('pages.adaComplianceGuide.hero.eyebrow')}
      title={t('pages.adaComplianceGuide.hero.title')}
      intro={t('pages.adaComplianceGuide.hero.intro')}
      metaType="article"
      structuredData={[
        buildArticleSchema({
          headline: t('pages.adaComplianceGuide.hero.title'),
          description: t('pages.adaComplianceGuide.meta.description'),
          path: GUIDE_PATH,
          datePublished: GUIDE_DATE,
          dateModified: GUIDE_DATE,
        }),
      ]}
      aside={(
        <>
          <section className="acc-public-page-card acc-public-page-card--sticky">
            <h2>{t('pages.adaComplianceGuide.sidebar.title')}</h2>
            <p>{t('pages.adaComplianceGuide.sidebar.body')}</p>
            <ul className="acc-public-page-list">
              {sidebarPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </section>

          <section className="acc-public-page-card">
            <h2>{t('pages.adaComplianceGuide.sidebar.noteTitle')}</h2>
            <p>{t('pages.adaComplianceGuide.sidebar.noteBody')}</p>
          </section>
        </>
      )}
    >
      <section className="acc-public-page-card">
        <h2>{t('pages.adaComplianceGuide.intro.title')}</h2>
        <p>{t('pages.adaComplianceGuide.intro.body')}</p>
        <p>{t('pages.adaComplianceGuide.intro.reference')}</p>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.adaComplianceGuide.meaning.title')}</h2>
        <p>{t('pages.adaComplianceGuide.meaning.body')}</p>
        <ul className="acc-public-page-list">
          {(t('pages.adaComplianceGuide.meaning.points', { returnObjects: true }) as string[]).map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.adaComplianceGuide.checklist.title')}</h2>
        <ul className="acc-public-page-list" aria-label={t('pages.adaComplianceGuide.checklist.ariaLabel')}>
          {checklist.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="acc-public-page-section" aria-labelledby="ada-process-title">
        <div>
          <h2 id="ada-process-title">{t('pages.adaComplianceGuide.process.title')}</h2>
          <p>{t('pages.adaComplianceGuide.process.body')}</p>
        </div>

        <div className="acc-public-page-grid">
          {steps.map((step, index) => (
            <article key={step.title} className="acc-public-page-subcard">
              <h3>
                {t('pages.adaComplianceGuide.process.stepLabel', { number: index + 1 })}
                {' '}
                {step.title}
              </h3>
              <p>{step.body}</p>
            </article>
          ))}
        </div>
      </section>

      {sections.map((section) => (
        <section key={section.title} className="acc-public-page-card">
          <h2>{section.title}</h2>
          <p>{section.body}</p>
          {section.points && section.points.length > 0 && (
            <ul className="acc-public-page-list">
              {section.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <section className="acc-public-page-card">
        <h2>{t('pages.adaComplianceGuide.disclaimer.title')}</h2>
        <p>{t('pages.adaComplianceGuide.disclaimer.body')}</p>
      </section>

      <section className="acc-public-cta-band" aria-labelledby="ada-guide-cta-title">
        <div>
          <h2 id="ada-guide-cta-title">{t('pages.adaComplianceGuide.cta.title')}</h2>
          <p>{t('pages.adaComplianceGuide.cta.body')}</p>
        </div>
        <div className="acc-public-cta-actions">
          {ctaActions.map((action) => (
            <PublicCtaLink key={action.href} href={action.href} variant={action.variant}>
              {action.label}
            </PublicCtaLink>
          ))}
        </div>
      </section>
    </PublicContentPage>
  );
}
